'use client';
// ---------------------------------------------------------------------------
// Library undo — CMD+Z / CMD+SHIFT+Z over the store's command seam (spec
// §5.4). Mounted by the library surface only, so the shortcut never fights
// the canvas editor's own undo stack. Text fields keep their native undo:
// events from inputs/textareas/contenteditable are ignored.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { useLibraryStore } from '@/store/libraryStore';
function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
    return target.isContentEditable;
}
export function useLibraryUndo() {
    const t = useTranslations$1();
    const [toast, setToast] = useState<{
        kind: 'undo' | 'redo';
        label: string;
    } | null>(null);
    useEffect(()=>{
        if (!toast) return;
        const timer = setTimeout(()=>setToast(null), 2200);
        return ()=>clearTimeout(timer);
    }, [
        toast
    ]);
    const handler = useCallback((e: KeyboardEvent)=>{
        if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        const s = useLibraryStore.getState();
        if (e.shiftKey) {
            const label = s.redo();
            if (label) setToast({
                kind: 'redo',
                label
            });
        } else {
            const label = s.undo();
            if (label) setToast({
                kind: 'undo',
                label
            });
        }
    }, []);
    useEffect(()=>{
        window.addEventListener('keydown', handler);
        return ()=>window.removeEventListener('keydown', handler);
    }, [
        handler
    ]);
    const undoToast = toast ? (<div className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
      <div className="rounded-md border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-lg">
        {toast.kind === 'undo' ? t("jRHFVv", {
        action: toast.label
    }) : t("r7LoGn", {
        action: toast.label
    })}
      </div>
    </div>) : null;
    return {
        undoToast
    };
}
